import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { FaCloudUploadAlt, FaFileAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { submitCandidate, uploadSourceCv } from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

const SubmitCandidateModal = ({ isOpen, onClose, incomingId, onSuccess }) => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId } = useBusinessUnit();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    costAmount: "",
    costType: "",
    availabilityDate: "",
  });
  const [sourceCvFile, setSourceCvFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const resetForm = () => {
    setForm({ costAmount: "", costType: "", availabilityDate: "" });
    setSourceCvFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!sourceCvFile) {
      toast({
        title: t("partners.incoming.sourceCvRequired", "Please upload an anonymized CV"),
        status: "warning",
        duration: 3000,
      });
      return;
    }
    setLoading(true);
    try {
      const uploadResult = await uploadSourceCv(sourceCvFile, { buId: selectedBusinessUnitId || undefined });
      const sourceCvUrl = uploadResult.url;

      const payload = {
        sourceCvUrl,
        costAmount: form.costAmount ? parseInt(form.costAmount, 10) : undefined,
        costType: form.costType || undefined,
        availabilityDate: form.availabilityDate || undefined,
      };

      await submitCandidate(
        incomingId,
        payload,
        { buId: selectedBusinessUnitId || undefined }
      );
      toast({ title: t("partners.incoming.candidateSubmitted", "Candidate submitted successfully"), status: "success", duration: 3000 });
      resetForm();
      onSuccess?.();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error";
      toast({ title: detail, status: "error", duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>{t("partners.incoming.submitCandidate", "Submit Candidate")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            {/* Source CV upload */}
            <FormControl isRequired>
              <FormLabel>{t("partners.incoming.sourceCv", "CV")}</FormLabel>
              <Box
                border="2px dashed"
                borderColor="gray.200"
                borderRadius="md"
                p={3}
                textAlign="center"
                cursor="pointer"
                onClick={() => fileInputRef.current?.click()}
                _hover={{ bg: "gray.50" }}
              >
                <VStack spacing={1}>
                  <Icon as={FaCloudUploadAlt} boxSize={5} color="gray.400" />
                  <Text fontSize="sm" color="gray.500" sx={{ marginBottom: "0 !important" }}>
                    {t("partners.incoming.uploadCv", "Click to upload CV")}
                  </Text>
                  <Text fontSize="xs" color="gray.400" sx={{ marginBottom: "0 !important" }}>
                    PDF, DOC, DOCX (max 10 MB)
                  </Text>
                </VStack>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0] || null;
                    if (file && file.size > 10 * 1024 * 1024) {
                      toast({ title: t("partners.incoming.fileTooLarge", "File too large (max 10 MB)"), status: "warning", duration: 3000 });
                      e.target.value = "";
                      return;
                    }
                    setSourceCvFile(file);
                  }}
                />
              </Box>
              {sourceCvFile && (
                <Tag size="md" variant="subtle" colorScheme="blue" mt={2}>
                  <Icon as={FaFileAlt} mr={1} />
                  <TagLabel>{sourceCvFile.name}</TagLabel>
                  <TagCloseButton onClick={() => { setSourceCvFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }} />
                </Tag>
              )}
            </FormControl>

            {/* Cost */}
            <HStack spacing={3}>
              <FormControl>
                <FormLabel>{t("partners.incoming.costAmount", "Cost")}</FormLabel>
                <Input
                  type="number"
                  value={form.costAmount}
                  onChange={handleChange("costAmount")}
                  placeholder="0"
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t("partners.incoming.costType", "Cost Type")}</FormLabel>
                <Select
                  value={form.costType}
                  onChange={handleChange("costType")}
                  placeholder={t("partners.incoming.selectCostType", "Select type")}
                >
                  <option value="salary">{t("partners.incoming.salary", "Salary")}</option>
                  <option value="daily_rate">{t("partners.incoming.dailyRate", "Daily Rate")}</option>
                </Select>
              </FormControl>
            </HStack>

            {/* Availability date */}
            <FormControl>
              <FormLabel>{t("partners.incoming.availabilityDate", "Availability Date")}</FormLabel>
              <Input
                type="date"
                value={form.availabilityDate}
                onChange={handleChange("availabilityDate")}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button variant="secondary" size="sm" onClick={onClose}>{t("common.cancel", "Cancel")}</Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} isLoading={loading}>
            {t("partners.incoming.submit", "Submit")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SubmitCandidateModal;
